import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getALOPendingReviews, resolveHierarchy, getHierarchyLogs, getOrganizations } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Building2,
  AlertTriangle,
  CheckCircle,
  GitMerge,
  Link2,
  Link2Off,
  Loader2,
  Search,
  Clock,
  Eye,
  ChevronRight,
  History,
  Shield,
  Users,
  RefreshCw
} from 'lucide-react';

export default function ALOAdmin() {
  const { user } = useAuth();
  const [pendingReviews, setPendingReviews] = useState([]);
  const [hierarchyLogs, setHierarchyLogs] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null, org: null });
  const [processing, setProcessing] = useState(false);

  // For merge/assign parent
  const [targetOrgId, setTargetOrgId] = useState('');
  const [orgSearch, setOrgSearch] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, logsRes, orgsRes] = await Promise.all([
        getALOPendingReviews().catch(() => ({ data: [] })),
        getHierarchyLogs({ limit: 50 }).catch(() => ({ data: [] })),
        getOrganizations().catch(() => ({ data: [] }))
      ]);
      setPendingReviews(reviewsRes.data || []);
      setHierarchyLogs(logsRes.data || []);
      setOrganizations(orgsRes.data || []);
    } catch (error) {
      console.error('Error loading ALO admin data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (action, org) => {
    setSelectedOrg(org);
    setTargetOrgId('');
    setActionDialog({ open: true, action, org });
  };

  const executeAction = async () => {
    if (!actionDialog.action || !actionDialog.org) return;
    
    setProcessing(true);
    try {
      const params = {};
      if (actionDialog.action === 'merge') {
        if (!targetOrgId) {
          toast.error('Please select an organization to merge into');
          return;
        }
        params.merge_into_org_id = targetOrgId;
      } else if (actionDialog.action === 'assign_parent') {
        if (!targetOrgId) {
          toast.error('Please select a parent organization');
          return;
        }
        params.parent_org_id = targetOrgId;
      }

      await resolveHierarchy(actionDialog.org.organization.id, actionDialog.action, params);
      toast.success(`Successfully ${actionDialog.action === 'merge' ? 'merged' : actionDialog.action === 'assign_parent' ? 'assigned parent' : 'confirmed standalone'}`);
      setActionDialog({ open: false, action: null, org: null });
      loadData();
    } catch (error) {
      console.error('Error executing action:', error);
      toast.error(error.response?.data?.detail || 'Failed to execute action');
    } finally {
      setProcessing(false);
    }
  };

  const filteredOrgs = organizations.filter(org => {
    if (!orgSearch) return true;
    return org.name.toLowerCase().includes(orgSearch.toLowerCase());
  });

  const parentLevelOrgs = organizations.filter(org => 
    ['national', 'regional', 'state'].includes(org.level)
  );

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-8">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This page is only accessible to Super Admins.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alo-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Assessment-Led Onboarding Admin</h1>
          <p className="text-muted-foreground">
            Manage hierarchy resolution and review organizations that joined via external assessment
          </p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Reviews</p>
                <p className="text-2xl font-bold">{pendingReviews.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">ALO Organizations</p>
                <p className="text-2xl font-bold">
                  {organizations.filter(o => o.onboarding_path === 'assessment_led').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Resolved Today</p>
                <p className="text-2xl font-bold">
                  {hierarchyLogs.filter(l => {
                    const today = new Date().toISOString().split('T')[0];
                    return l.created_at?.startsWith(today);
                  }).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <History className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Resolutions</p>
                <p className="text-2xl font-bold">{hierarchyLogs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">
            Pending Reviews ({pendingReviews.length})
          </TabsTrigger>
          <TabsTrigger value="logs">
            Resolution History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : pendingReviews.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-semibold">All Caught Up!</h3>
                <p className="text-muted-foreground">
                  There are no pending hierarchy reviews at this time.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingReviews.map((review) => (
                <Card key={review.organization?.id} className="border-amber-200 bg-amber-50/30">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-3 flex-1">
                        <div className="flex items-center gap-3">
                          <Building2 className="w-5 h-5 text-slate-500" />
                          <div>
                            <h3 className="font-semibold">{review.organization?.name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {review.organization?.city}, {review.organization?.state}
                            </p>
                          </div>
                          <Badge variant="outline" className="capitalize">
                            {review.organization?.org_type}
                          </Badge>
                          <Badge className="bg-amber-100 text-amber-700">
                            Needs Review
                          </Badge>
                        </div>

                        {/* Potential Duplicate Alert */}
                        {review.potential_duplicate && (
                          <Alert className="bg-amber-50 border-amber-200">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <AlertDescription className="text-amber-800">
                              <strong>Potential duplicate found:</strong> {review.potential_duplicate.name}
                              <span className="text-amber-600 ml-2">
                                ({review.potential_duplicate.city}, {review.potential_duplicate.state})
                              </span>
                            </AlertDescription>
                          </Alert>
                        )}

                        {/* Hierarchy Resolution Details */}
                        {review.hierarchy_resolution && (
                          <div className="text-sm bg-white p-3 rounded-lg border">
                            <p className="font-medium mb-2">Resolution Details:</p>
                            <ul className="space-y-1 text-muted-foreground">
                              <li>• Method: {review.hierarchy_resolution.resolution_method}</li>
                              <li>• Confidence: {(review.hierarchy_resolution.confidence_score * 100).toFixed(0)}%</li>
                              {review.hierarchy_resolution.parent_org_name && (
                                <li>• Suggested Parent: {review.hierarchy_resolution.parent_org_name}</li>
                              )}
                              {review.hierarchy_resolution.audit_notes?.map((note, i) => (
                                <li key={i}>• {note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-2 ml-4">
                        {review.potential_duplicate && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            onClick={() => handleAction('merge', review)}
                          >
                            <GitMerge className="w-4 h-4 mr-2" />
                            Merge
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAction('assign_parent', review)}
                        >
                          <Link2 className="w-4 h-4 mr-2" />
                          Assign Parent
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAction('standalone', review)}
                        >
                          <Link2Off className="w-4 h-4 mr-2" />
                          Standalone
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="logs" className="space-y-4">
          {hierarchyLogs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <History className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold">No Resolution History</h3>
                <p className="text-muted-foreground">
                  Hierarchy resolution logs will appear here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Resolution History</CardTitle>
                <CardDescription>Recent hierarchy resolution actions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {hierarchyLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          log.action === 'hierarchy_resolved' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          {log.resolved_parent_id ? (
                            <Link2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <Building2 className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{log.action}</p>
                          <p className="text-sm text-muted-foreground">
                            Org: {log.organization_id?.slice(0, 8)}...
                            {log.resolved_parent_id && ` → Parent: ${log.resolved_parent_id.slice(0, 8)}...`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline">
                          {(log.confidence_score * 100).toFixed(0)}% confidence
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(log.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !processing && setActionDialog({ ...actionDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.action === 'merge' && 'Merge Organization'}
              {actionDialog.action === 'assign_parent' && 'Assign Parent Organization'}
              {actionDialog.action === 'standalone' && 'Confirm Standalone Organization'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.action === 'merge' && 
                'Merge this organization into an existing one. All assessments and users will be moved.'
              }
              {actionDialog.action === 'assign_parent' && 
                'Assign a parent organization to establish hierarchy.'
              }
              {actionDialog.action === 'standalone' && 
                'Confirm this organization operates independently without a parent.'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Organization being acted upon */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <Label className="text-xs text-muted-foreground">Organization</Label>
              <p className="font-medium">{actionDialog.org?.organization?.name}</p>
              <p className="text-sm text-muted-foreground">
                {actionDialog.org?.organization?.city}, {actionDialog.org?.organization?.state}
              </p>
            </div>

            {/* Target org selection for merge/assign_parent */}
            {(actionDialog.action === 'merge' || actionDialog.action === 'assign_parent') && (
              <div className="space-y-2">
                <Label>
                  {actionDialog.action === 'merge' ? 'Merge Into' : 'Parent Organization'}
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    placeholder="Search organizations..."
                    className="pl-10"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  {(actionDialog.action === 'assign_parent' ? parentLevelOrgs : filteredOrgs)
                    .filter(o => o.id !== actionDialog.org?.organization?.id)
                    .slice(0, 10)
                    .map(org => (
                      <button
                        key={org.id}
                        onClick={() => setTargetOrgId(org.id)}
                        className={`w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center justify-between ${
                          targetOrgId === org.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div>
                          <p className="font-medium">{org.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {org.level} • {org.city}, {org.state}
                          </p>
                        </div>
                        {targetOrgId === org.id && (
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                        )}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {actionDialog.action === 'standalone' && (
              <Alert>
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  This organization will be confirmed as standalone with no parent organization.
                  This action can be changed later if needed.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActionDialog({ open: false, action: null, org: null })}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={processing || ((actionDialog.action === 'merge' || actionDialog.action === 'assign_parent') && !targetOrgId)}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
